import {
  animate,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  state,
  style,
  transition,
  trigger,
  ViewEncapsulation
} from "@angular/core";
import { TableColumnDirective } from "./table-column.directive";
import { Observable } from "rxjs";
import { TableColumnContentDirective } from "./table-column-content.directive";
import { FormControl } from "@angular/forms";
import { JsonApiModel, ModelType } from "angular2-jsonapi";
import { DataStoreService } from "../../services/api/data-store.service";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { Subject } from "rxjs/Subject";
import { ReplaySubject } from "rxjs/ReplaySubject";

export type TableData<T> = Observable<T[]> | T[];

enum DataState {
  LOADING,
  OK,
  ERROR
}

export interface SearchConfiguration<T> {
  idField: string,
  fields: { name: string, boost?: number }[],
  preIndexExtender?: (T) => any
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  encapsulation: ViewEncapsulation.None,

  animations: [ //
    trigger('flyIn', [
      state('in', style({ transform: 'translateY(0)', opacity: 1 })),
      transition('void => *', [
        style({ transform: 'translateY(150%)', opacity: 0 }),
        animate("0.2s ease-out")
      ]),
      transition('* => void', [
        animate("0.2s ease-in", style({ transform: 'translateY(150%)', opacity: 0 }))
      ])
    ]),
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition('void => *', [
        style({ opacity: 0 }),
        animate("0.2s ease-out")
      ]),
      transition('* => void', [
        animate("0.2s ease-in", style({ opacity: 0 }))
      ])
    ]),
  ]
})
export class DataTableComponent<T extends JsonApiModel> implements OnChanges, OnInit {

  @ContentChildren(TableColumnDirective) columns: QueryList<TableColumnDirective>;

  @Input() data: TableData<T>;
  @Output() rowClick = new EventEmitter();
  @Input() model: ModelType<T> = null;
  @Input("class") cssClass: string = "";

  tableData: Array<T> = [];
  searchControl = new FormControl();
  state: DataState = DataState.LOADING;

  States = DataState;

  public pageSubject = new ReplaySubject();
  private searchQuery: Observable<string>;
  @Input() filters = new BehaviorSubject({});

  @Input()
  pageParam: string = "page";


  public readonly RECORDS_PER_PAGE = 20;
  private readonly DEFAULT_PAGE = 1;

  public nrOfRecords = 0;
  private loadSubscription;

  constructor(private dataStore: DataStoreService, private activatedRoute: ActivatedRoute,
              private router: Router) {
  }

  private queryParam = "query";

  ngOnInit(): void {
    this.addSearchObserver();
    if (this.model != null) {
      this.createReactiveDataSource();
      this.ngOnChanges(null);
    }

    if (this.rowClick.observers.length > 0) {
      this.cssClass += " clickable";
    }

    this.activatedRoute.paramMap.subscribe(params => {
      if (params.has(this.pageParam)) {
        this.pageSubject.next(parseInt(params.get(this.pageParam)))
      } else {
        this.pageSubject.next(this.DEFAULT_PAGE);
      }
      if (params.has(this.queryParam)) {
        this.searchControl.patchValue(params.get(this.queryParam));
      } else {
        this.searchControl.patchValue("");
      }
    });

  }


  private createReactiveDataSource() {
    let queryFilters = Observable.combineLatest(this.searchQuery.startWith(""), this.filters.startWith({}), (query, filters) => {
      let params = query != null && query != "" ? { query: query } : {};
      Object.assign(params, filters);
      return params;
    });
    this.data = this.pageSubject.startWith(this.DEFAULT_PAGE).distinctUntilChanged().combineLatest(queryFilters)
      .switchMap(request => {

        this.state = DataState.LOADING;
        if (Object.keys(request[1]).length > 0) {
          request[0] = 1;
        }
        this.routeToParameterized(request[0] as number, request[1]);
        return this.dataStore.findAll(this.model, {
          page: { size: this.RECORDS_PER_PAGE, number: request[0] },
          filter: request[1]
        })
      }).map(response => {
        this.nrOfRecords = response.getMeta().page.total;
        return response.getModels();
      });
  }


  private routeToParameterized(page: number, filter:any) {
    const extras: NavigationExtras = {
      fragment: this.activatedRoute.snapshot.fragment,
      queryParams: this.activatedRoute.snapshot.queryParams,
    };
    const params = Object.assign({}, this.activatedRoute.snapshot.params);
    if (page != 1) {
      params[this.pageParam] = page;
    } else {
      delete params[this.pageParam];
    }
    if (filter[this.queryParam]) {
      params[this.queryParam] = filter[this.queryParam];
    } else {
      delete params[this.queryParam];
    }
    this.router.navigate([params], extras);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (this.data instanceof Array) {
      this.state = DataState.OK;
      this.tableData = this.data;
    } else if (typeof(this.data) == "undefined") {
      this.state = DataState.LOADING;
    } else if (this.data instanceof Observable || this.data["subscribe"] !== undefined) {
      this.state = DataState.LOADING;
      if (this.loadSubscription) {
        this.loadSubscription.unsubscribe();
        this.loadSubscription = null;
      }
      this.loadSubscription = this.data.subscribe((result) => {
        this.tableData = result;
        this.state = DataState.OK;
      }, (error) => {
        console.error(error);
        this.state = DataState.ERROR;
      })
    } else {
      this.state = DataState.LOADING;
    }
  }


  private addSearchObserver() {
    this.searchQuery = this.searchControl.valueChanges.distinctUntilChanged().debounceTime(400);
  }


  getColumnContent(content: TableColumnContentDirective, record: any) {
    content.record = record;
    return content;
  }

  emitRowClick(event: Event, record: T) {
    // event.preventDefault();
    // event.stopPropagation();
    // TODO allow a tag propagation
    this.rowClick.emit(record);
  }

}



// WEBPACK FOOTER //
// /home/markush/Projects/fieldsale-wingman-client/src/app/shared/components/data-table/data-table.component.ts